import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from './supabase'
import { signInWithGoogle, signOut, syncAppUser, type AppUser } from './auth'

interface AuthContextValue {
  loading: boolean
  session: Session | null
  appUser: AppUser | null
  signIn: () => Promise<void>
  logOut: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true)
  const [session, setSession] = useState<Session | null>(null)
  const [appUser, setAppUser] = useState<AppUser | null>(null)

  const handleSession = async (newSession: Session | null) => {
    setSession(newSession)
    if (newSession?.user) {
      const meta = newSession.user.user_metadata || {}
      const record = await syncAppUser({
        email: newSession.user.email || '',
        name: meta.full_name || meta.name || null,
        avatar_url: meta.avatar_url || meta.picture || null,
      })
      // Don't clobber a previously-resolved approved user with a transient null
      // (can happen if a concurrent event's read momentarily fails).
      setAppUser(prev => record ?? prev)
    } else {
      setAppUser(null)
    }
    setLoading(false)
  }

  useEffect(() => {
    // Initial session check on mount
    supabase.auth.getSession().then(({ data }) => {
      handleSession(data.session)
    })

    // Listen for login/logout events (including OAuth redirect completion)
    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      handleSession(newSession)
    })

    return () => sub.subscription.unsubscribe()
  }, [])

  const refresh = async () => {
    const { data } = await supabase.auth.getSession()
    await handleSession(data.session)
  }

  const value: AuthContextValue = {
    loading,
    session,
    appUser,
    signIn: signInWithGoogle,
    logOut: async () => {
      await signOut()
      setSession(null)
      setAppUser(null)
    },
    refresh,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
